// Disciplinary: the hearing chair's recommendation after a disciplinary
// hearing, filed by the driver's manager and released for signature.
// db/migrations/2401 and 2403, docs/DISCIPLINARY-RECOMMENDATIONS.md.
//
// Asked for by Luyanda on 2 October 2026: Yolisa chairs hearings and sends her
// recommendation by email; the manager files it here with the document and,
// when ready, releases it. Release sends the chair's pages plus one
// acknowledgement page through SignNow, the manager first and the driver
// second, and puts the sanction on the driver's warning record from that day.
//
// Since 5 October 2026 (db/migrations/4400 to 4403, docs/DISCIPLINARY-INCIDENTS.md)
// the page opens on Suggested: disciplinary incidents reported in the cockpit,
// which replaced the Typeform on fleetly.co.za/discipline, and the cases the
// nightly checks raised, each with the step the agreement suggests, for the
// manager to release. Recommendations is the original page, and a hearing on a
// reported incident ends there, with the recommendation filed against it.
//
// This page decides nothing. Who may file, release or open a recommendation,
// and every refusal, is Postgres; the sentence it returns is shown as it comes.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { BellRing, Eye, FileText, Gavel, Pencil, Plus, RefreshCw, Send, ShieldAlert, Upload, X } from 'lucide-react'
import {
  useCheckDisciplinarySignatures, useFileDisciplinaryRecommendation, useGetDisciplinaryPreview,
  useGetDisciplinaryRecommendations, useGetDisciplinarySuggestions, useOpenDisciplinaryFile, useOpenDisciplinarySignedCopy,
  useReleaseDisciplinaryRecommendation, useRemindDisciplinaryRecommendation, useSignDisciplinaryUpload,
  useWithdrawDisciplinaryRecommendation,
} from '../hooks/backend/disciplinary'
import { Button } from '../lib/shadcn/button'
import { Input } from '../lib/shadcn/input'
import { Label } from '../lib/shadcn/label'
import { NativeSelect } from '../lib/shadcn/native-select'
import { Spinner } from '../lib/shadcn/spinner'
import { Textarea } from '../lib/shadcn/textarea'
import { toast } from '../lib/shadcn/sonner'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../lib/shadcn/dialog'
import { Chip } from '../components/interviews/Chip'
import { writeErrorMessage } from '../utils/writeError'
import { pdfBlobUrl } from '../utils/loans'
import { checkDocument, uploadSigned, type Signed } from '../utils/signedUpload'
import {
  day, isWarning, monthsParam, sanctionTone, statusText, todaySast, when, type DisciplinaryPage, type Recommendation,
} from '../utils/disciplinary'
import type { SuggestionsPage } from '../utils/disciplinaryIncidents'
import { IncidentReportDialog } from '../components/disciplinary/IncidentReportDialog'
import { SuggestionsPanel } from '../components/disciplinary/SuggestionsPanel'
import { AmendmentHistory, AmendPanel, DecidePanel, LetterPanel } from '../components/disciplinary/RecommendationSteps'

type Filter = 'open' | 'signed' | 'all'
type Tab = 'suggested' | 'recommendations'

export default function Disciplinary() {
  const q = useGetDisciplinaryRecommendations<DisciplinaryPage>()
  const sq = useGetDisciplinarySuggestions<SuggestionsPage>()
  const check = useCheckDisciplinarySignatures()
  const load = q.trigger
  const loadSuggestions = sq.trigger
  const [filing, setFiling] = useState(false)
  const [reporting, setReporting] = useState(false)
  const [filter, setFilter] = useState<Filter>('open')
  const [tab, setTab] = useState<Tab>('suggested')
  const checked = useRef(false)

  const refresh = useCallback(() => {
    void load({}, { skipCache: true }).catch(() => undefined)
    void loadSuggestions({}, { skipCache: true }).catch(() => undefined)
  }, [load, loadSuggestions])
  useEffect(() => { refresh() }, [refresh])

  const page = q.data
  // Opening the page asks SignNow once about anything out for signature, so a
  // manager standing beside the driver sees the signature land.
  useEffect(() => {
    if (!page || checked.current) return
    if (!page.recommendations.some((r) => r.status === 'sent' || r.status === 'manager_signed')) return
    checked.current = true
    void check.trigger({}).result.then(refresh).catch(() => undefined)
  }, [page, check, refresh])

  const shown = useMemo(() => {
    const all = page?.recommendations ?? []
    if (filter === 'all') return all
    if (filter === 'signed') return all.filter((r) => r.status === 'signed')
    // Signed but the warning letter not yet signed is still open work.
    return all.filter((r) => !['withdrawn', 'rejected'].includes(r.status) &&
      (r.status !== 'signed' || (!!r.letter?.status && r.letter.status !== 'signed')))
  }, [page, filter])

  if (q.error && !page) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <div className="flex max-w-md flex-col items-center gap-3 rounded-lg border border-border bg-card p-8 text-center">
          <ShieldAlert className="h-6 w-6 text-muted-foreground" />
          <p className="text-sm text-foreground">{q.error}</p>
          <Button size="sm" variant="outline" onClick={refresh}>Try again</Button>
        </div>
      </div>
    )
  }
  if (!page) {
    return <p className="flex items-center gap-2 p-6 text-sm text-muted-foreground"><Spinner className="h-4 w-4" /> Loading recommendations…</p>
  }

  const all = page.recommendations
  const counts = {
    open: all.filter((r) => !['withdrawn', 'rejected'].includes(r.status) &&
      (r.status !== 'signed' || (!!r.letter?.status && r.letter.status !== 'signed'))).length,
    signed: all.filter((r) => r.status === 'signed').length,
  }
  const sp = sq.data
  const waiting = sp ? sp.automated.length + sp.incidents.filter((i) => i.status === 'reported').length : null

  return (
    <div className="flex h-full min-h-0 flex-col bg-card">
      <div className="flex flex-wrap items-center gap-3 border-b border-border px-4 py-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]">
          <Gavel className="h-5 w-5" />
        </div>
        <div className="min-w-[12rem] flex-1">
          <h1 className="text-lg font-semibold text-foreground">Disciplinary</h1>
          <p className="text-xs text-muted-foreground">
            {tab === 'suggested'
              ? 'Incidents reported by staff and cases raised by the nightly checks, each with the step the agreement suggests. Nothing reaches the driver until his manager releases it.'
              : 'The chair\'s recommendation after a disciplinary hearing. File it, accept or reject it, then release what was accepted: you sign first, then the driver signs for receipt. After that, send the warning letter itself.'}
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={refresh} aria-label="Refresh">
          <RefreshCw className="h-4 w-4" />
        </Button>
        <Button variant={tab === 'suggested' ? 'brand' : 'outline'} size="sm" onClick={() => setReporting(true)}>
          <Plus className="mr-1 h-4 w-4" /> Report an incident
        </Button>
        <Button variant={tab === 'recommendations' ? 'brand' : 'outline'} size="sm" onClick={() => setFiling(true)} disabled={page.drivers.length === 0}>
          <Upload className="mr-1 h-4 w-4" /> File a recommendation
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-1 border-b border-border px-4 pt-2">
        {(['suggested', 'recommendations'] as Tab[]).map((t) => (
          <button key={t} type="button" onClick={() => setTab(t)}
            className={`-mb-px border-b-2 px-3 pb-2 text-sm font-medium transition-colors ${tab === t ? 'border-[hsl(var(--brand))] text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>
            {t === 'suggested' ? `Suggested${waiting ? ` (${waiting})` : ''}` : `Recommendations${counts.open ? ` (${counts.open})` : ''}`}
          </button>
        ))}
      </div>

      {tab === 'suggested' ? (
        <div className="min-h-0 flex-1 overflow-y-auto">
          <div className="mx-auto max-w-4xl p-4">
            {sq.error && !sp ? (
              <div className="flex flex-col items-center gap-3 rounded-lg border border-border p-6 text-center">
                <p className="text-sm text-foreground">{sq.error}</p>
                <Button size="sm" variant="outline" onClick={refresh}>Try again</Button>
              </div>
            ) : !sp ? (
              <p className="flex items-center gap-2 text-sm text-muted-foreground"><Spinner className="h-4 w-4" /> Loading suggestions…</p>
            ) : (
              <SuggestionsPanel page={sp} onChanged={refresh} />
            )}
          </div>
        </div>
      ) : (<>
      <div className="flex flex-wrap items-center gap-2 border-b border-border px-4 py-2">
        {(['open', 'signed', 'all'] as Filter[]).map((f) => (
          <Button key={f} size="sm" variant={filter === f ? 'brand' : 'ghost'} className="h-7 px-2.5 text-xs" onClick={() => setFilter(f)}>
            {f === 'open' ? `Open (${counts.open})` : f === 'signed' ? `Signed (${counts.signed})` : `All (${all.length})`}
          </Button>
        ))}
        {!page.sees_all && (
          <span className="text-xs text-muted-foreground">Showing the drivers you manage.</span>
        )}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto max-w-4xl space-y-3 p-4">
          {page.drivers.length === 0 && (
            <p className="rounded-md border border-border bg-muted/40 p-3 text-sm text-muted-foreground">
              You do not manage any drivers, so there is nobody you can file a recommendation for. The driver's manager, the hearing chair or an administrator can.
            </p>
          )}
          {shown.length === 0 ? (
            <p className="py-10 text-center text-sm text-muted-foreground">
              {filter === 'open' ? 'No recommendation is waiting to be accepted, released, signed or followed by its warning letter.' : 'Nothing here yet.'}
            </p>
          ) : (
            shown.map((r) => <RecommendationCard key={r.id} r={r} sanctions={page.sanctions} onChanged={refresh} />)
          )}
        </div>
      </div>
      </>)}

      <FileDialog open={filing} onOpenChange={setFiling} page={page} onFiled={() => { setFilter('open'); setTab('recommendations'); refresh() }} />
      <IncidentReportDialog open={reporting} onOpenChange={setReporting} onReported={() => { setTab('suggested'); refresh() }} />
    </div>
  )
}

function RecommendationCard({ r, sanctions, onChanged }: { r: Recommendation; sanctions: DisciplinaryPage['sanctions']; onChanged: () => void }) {
  const openFile = useOpenDisciplinaryFile<{ url: string }>()
  const preview = useGetDisciplinaryPreview<{ pdf_base64: string; pages: number }>()
  const release = useReleaseDisciplinaryRecommendation()
  const remind = useRemindDisciplinaryRecommendation()
  const withdraw = useWithdrawDisciplinaryRecommendation()
  const signed = useOpenDisciplinarySignedCopy<{ url: string }>()
  const [busy, setBusy] = useState<string | null>(null)
  const [confirming, setConfirming] = useState(false)
  const [withdrawing, setWithdrawing] = useState(false)
  const [deciding, setDeciding] = useState(false)
  const [amending, setAmending] = useState(false)
  const [reason, setReason] = useState('')
  const st = statusText(r)
  const adopted = r.accepted_text && r.accepted_text !== r.recommended_text

  async function run(label: string, fn: () => Promise<unknown>, done?: string) {
    setBusy(label)
    try {
      await fn()
      if (done) toast.success(done)
      onChanged()
    } catch (err) {
      toast.error(writeErrorMessage(err))
    } finally {
      setBusy(null)
    }
  }

  // Only what was accepted is released (4800).
  const canRelease = r.status === 'accepted' || r.status === 'declined' ||
    (r.status === 'sending' && !!r.released_at && Date.now() - new Date(r.released_at).getTime() > 10 * 60 * 1000)
  const out = r.status === 'sent' || r.status === 'manager_signed'

  return (
    <article className="space-y-2 rounded-lg border border-border bg-background p-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-semibold text-foreground">{r.driver ?? 'Driver'}</span>
        <span className="text-xs text-muted-foreground">{r.ref}</span>
        {r.incident_ref && <span className="text-xs text-muted-foreground">for incident {r.incident_ref}</span>}
        <Chip tone={sanctionTone(r.accepted_sanction ?? r.sanction)}>{r.accepted_text ?? r.recommended_text}</Chip>
        <Chip tone={st.tone}>{st.text}</Chip>
      </div>
      <p className="text-sm text-foreground">{r.charge}</p>
      <p className="text-xs text-muted-foreground">
        The chair recommended <span className="text-foreground">{r.recommended_text.toLowerCase()}</span>
        {r.recommended_months == null && isWarning(r.sanction) ? ' (no period was recorded when it was filed)' : ''}.
        {r.decision === 'accepted' && <> Accepted by {r.decided_by ?? 'unknown'}, {when(r.decided_at)}{adopted ? <>, as <span className="font-medium text-foreground">{r.accepted_text?.toLowerCase()}</span></> : ' as recommended'}.</>}
        {r.decision === 'rejected' && <> Rejected by {r.decided_by ?? 'unknown'}, {when(r.decided_at)}.</>}
        {r.decision_note ? ` ${r.decision_note}` : ''}
      </p>
      {r.summary && <p className="text-xs text-muted-foreground">{r.summary}</p>}
      <p className="text-xs text-muted-foreground">
        Hearing {day(r.hearing_on)}, chaired by {r.chair ?? 'unknown'} · recommendation dated {day(r.recommended_on)} · filed by {r.filed_by ?? 'unknown'}, {when(r.filed_at)}
      </p>
      {r.released_at && (
        <p className="text-xs text-muted-foreground">
          Released by {r.released_by ?? 'unknown'}, {when(r.released_at)}.
          {r.on_register ? ` On the driver's record${r.lapses_on ? ` until ${day(r.lapses_on)}` : ''}.` : ''}
          {r.sent_at ? ` Sent ${when(r.sent_at)}.` : ''}
          {r.manager_signed_at ? ` ${r.manager ?? 'Manager'} signed ${when(r.manager_signed_at)}.` : ''}
          {r.signed_at ? ` Driver signed ${when(r.signed_at)}.` : ''}
          {r.reminders_sent > 0 ? ` ${r.reminders_sent} reminder${r.reminders_sent === 1 ? '' : 's'}, last ${when(r.last_reminded_at)}.` : ''}
        </p>
      )}
      {r.driver_comment && (
        <p className="rounded-md border border-border bg-muted/40 p-2 text-xs text-foreground"><span className="font-medium">The driver wrote: </span>{r.driver_comment}</p>
      )}
      {r.send_error && <p className="text-xs text-rose-700 dark:text-rose-300">{r.send_error}</p>}
      {r.last_remind_error && <p className="text-xs text-rose-700 dark:text-rose-300">{r.last_remind_error}</p>}
      {r.status === 'withdrawn' && r.withdrawn_reason && <p className="text-xs text-muted-foreground">Withdrawn {when(r.withdrawn_at)}: {r.withdrawn_reason}</p>}
      <AmendmentHistory r={r} />
      <LetterPanel r={r} onChanged={onChanged} />

      <div className="flex flex-wrap gap-2 pt-1">
        <Button size="sm" variant="outline" disabled={!!busy}
          onClick={() => void run('file', async () => {
            const x = await openFile.trigger({ path: r.file_path }).result
            if (x?.url) window.open(x.url, '_blank', 'noopener')
          })}>
          <FileText className="mr-1 h-3.5 w-3.5" /> The recommendation
        </Button>
        {r.status !== 'withdrawn' && (
          <Button size="sm" variant="outline" disabled={!!busy}
            onClick={() => void run('preview', async () => {
              const x = await preview.trigger({ id: r.id }, { skipCache: true })
              if (!x?.pdf_base64) throw new Error('The document could not be put together. Try again.')
              window.open(pdfBlobUrl(x.pdf_base64), '_blank', 'noopener')
            })}>
            {busy === 'preview' ? <Spinner className="mr-1 h-3.5 w-3.5" /> : <Eye className="mr-1 h-3.5 w-3.5" />} Preview what is signed
          </Button>
        )}
        {canRelease && !confirming && (
          <Button size="sm" variant="brand" disabled={!!busy} onClick={() => setConfirming(true)}>
            <Send className="mr-1 h-3.5 w-3.5" /> {r.status === 'declined' || r.status === 'sending' ? 'Release again' : 'Release for signature'}
          </Button>
        )}
        {out && (
          <Button size="sm" variant="outline" disabled={!!busy}
            onClick={() => void run('remind', () => remind.trigger({ id: r.id }).result, 'SignNow is sending the reminder.')}>
            <BellRing className="mr-1 h-3.5 w-3.5" /> Send a reminder
          </Button>
        )}
        {r.has_envelope && (
          <Button size="sm" variant="outline" disabled={!!busy}
            onClick={() => void run('signed', async () => {
              const x = await signed.trigger({ id: r.id }).result
              if (x?.url) window.open(x.url, '_blank', 'noopener')
            })}>
            <FileText className="mr-1 h-3.5 w-3.5" /> {r.status === 'signed' ? 'The signed copy' : 'The document in SignNow'}
          </Button>
        )}
        {r.can_decide && ['filed', 'accepted', 'rejected'].includes(r.status) && !deciding && (
          <Button size="sm" variant={r.status === 'filed' ? 'brand' : 'outline'} disabled={!!busy} onClick={() => setDeciding(true)}>
            <Gavel className="mr-1 h-3.5 w-3.5" /> {r.status === 'filed' ? 'Accept or reject' : 'Change the decision'}
          </Button>
        )}
        {r.can_decide && !!r.released_at && !['withdrawn', 'rejected'].includes(r.status) && !amending && (
          <Button size="sm" variant="outline" disabled={!!busy} onClick={() => setAmending(true)}>
            <Pencil className="mr-1 h-3.5 w-3.5" /> Amend
          </Button>
        )}
        {['filed', 'accepted', 'rejected'].includes(r.status) && !withdrawing && (
          <Button size="sm" variant="ghost" disabled={!!busy} onClick={() => setWithdrawing(true)}>
            <X className="mr-1 h-3.5 w-3.5" /> Withdraw
          </Button>
        )}
      </div>

      {deciding && <DecidePanel r={r} sanctions={sanctions} onChanged={onChanged} onClose={() => setDeciding(false)} />}
      {amending && <AmendPanel r={r} sanctions={sanctions} onChanged={onChanged} onClose={() => setAmending(false)} />}
      {confirming && (
        <div className="space-y-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-950 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100">
          <p>
            SignNow emails you first to sign as the manager issuing it, then {r.driver ?? 'the driver'} to sign for receipt.
            {r.accepted_sanction && r.accepted_sanction !== 'no_sanction' && r.accepted_sanction !== 'termination' && r.status === 'accepted' ? ` ${r.accepted_text} goes onto ${r.driver ?? 'the driver'}'s record today, whether or not they sign.` : ''}
            {/* 9301: a dismissal takes effect on the chair's date at release; the
                signature is a receipt and nothing waits for it. */}
            {r.accepted_sanction === 'termination' ? ` The dismissal takes effect at release, dated ${r.recommended_on ? day(r.recommended_on) : 'the day the chair made the recommendation'}, and ${r.driver ?? 'the driver'}'s offboarding case opens with it. Nothing waits for their signature, which is only proof they received it.` : ''}
          </p>
          <div className="flex gap-2">
            <Button size="sm" variant="brand" disabled={!!busy}
              onClick={() => void run('release', async () => {
                const x = await release.trigger({ id: r.id }).result as { offboarding?: { ok?: boolean; reason?: string; case_ref?: string; opened?: boolean } | null } | undefined
                setConfirming(false)
                const o = x?.offboarding
                if (o && o.ok === false && o.reason) toast.error(o.reason)
                else if (o?.case_ref) toast.success(`Offboarding case ${o.case_ref} ${o.opened ? 'opened' : 'brought into line'} for the dismissal.`)
              }, 'Released. SignNow is sending it; refresh in a minute to see it out for signature.')}>
              {busy === 'release' && <Spinner className="mr-1 h-3.5 w-3.5" />} Release it
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setConfirming(false)}>Not yet</Button>
          </div>
        </div>
      )}
      {withdrawing && (
        <div className="space-y-2">
          <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why it is being withdrawn, for example filed against the wrong driver" />
          <div className="flex gap-2">
            <Button size="sm" variant="outline" disabled={!reason.trim() || !!busy}
              onClick={() => void run('withdraw', async () => { await withdraw.trigger({ id: r.id, reason }).result; setWithdrawing(false) }, 'Withdrawn.')}>
              Withdraw it
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setWithdrawing(false)}>Cancel</Button>
          </div>
        </div>
      )}
    </article>
  )
}

function FileDialog({ open, onOpenChange, page, onFiled }: {
  open: boolean; onOpenChange: (v: boolean) => void; page: DisciplinaryPage; onFiled: () => void
}) {
  const sign = useSignDisciplinaryUpload<Signed>()
  const file = useFileDisciplinaryRecommendation()
  const defaultChair = page.chairs.find((c) => c.is_chair)?.id ?? ''
  const blank = () => ({ driver_id: '', case_id: '', hearing_on: '', chair_person_id: defaultChair, recommended_on: todaySast(), sanction: '', recommended_months: '', charge: '', summary: '' })
  const [f, setF] = useState(blank)
  const [doc, setDoc] = useState<File | null>(null)
  const [busy, setBusy] = useState(false)
  const set = (k: keyof ReturnType<typeof blank>) => (e: { target: { value: string } }) => setF((x) => ({ ...x, [k]: e.target.value }))
  const docProblem = doc ? checkDocument(doc, { pdfJpgPngOnly: true }) : null
  const ready = f.driver_id && f.hearing_on && f.chair_person_id && f.sanction && f.charge.trim() && doc && !docProblem &&
    (!isWarning(f.sanction) || (Number(f.recommended_months) >= 1 && Number(f.recommended_months) <= 24))
  // A hearing on a reported incident is filed against it, so releasing the
  // recommendation decides the incident too (4402).
  const cases = (page.hearing_cases ?? []).filter((c) => c.driver_id === f.driver_id)
  const linked = cases.find((c) => c.case_id === f.case_id)

  async function submit() {
    if (!doc) return
    setBusy(true)
    try {
      const path = await uploadSigned(await sign.trigger({ driver_id: f.driver_id, filename: doc.name }, { skipCache: true }), doc)
      await file.trigger({ ...f, recommended_months: isWarning(f.sanction) ? monthsParam(f.recommended_months) : null, case_id: f.case_id || null, recommended_on: f.recommended_on || null, summary: f.summary || null, path, filename: doc.name }).result
      toast.success('Recommendation filed. Accept or reject it next; only an accepted one can be released.')
      setF(blank())
      setDoc(null)
      onOpenChange(false)
      onFiled()
    } catch (err) {
      toast.error(writeErrorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>File a disciplinary recommendation</DialogTitle>
          <DialogDescription>
            The chair's recommendation after the hearing, with the document they sent. Filing puts nothing on the driver's record. Next it is accepted or rejected, and only an accepted one is released onto the record.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor="dr-driver">Driver</Label>
            <NativeSelect id="dr-driver" value={f.driver_id} onChange={(e) => setF((x) => ({ ...x, driver_id: e.target.value, case_id: '' }))}>
              <option value="">Choose the driver</option>
              {page.drivers.map((d) => <option key={d.id} value={d.id}>{d.name ?? 'Unnamed'}{d.status !== 'active' ? ` (${d.status})` : ''}</option>)}
            </NativeSelect>
          </div>
          {cases.length > 0 && (
            <div className="space-y-1">
              <Label htmlFor="dr-case">Which incident was the hearing about?</Label>
              <NativeSelect id="dr-case" value={f.case_id}
                onChange={(e) => { const c = cases.find((x) => x.case_id === e.target.value); setF((x) => ({ ...x, case_id: e.target.value, charge: x.charge || (c ? c.charge.slice(0, 600) : '') })) }}>
                <option value="">None: this hearing began outside the cockpit</option>
                {cases.map((c) => <option key={c.case_id} value={c.case_id}>{c.ref}, {day(c.occurred_on)} (the most it can decide: {c.proposed_label.toLowerCase()})</option>)}
              </NativeSelect>
              {linked && (
                <p className="text-xs text-muted-foreground">
                  Releasing this recommendation decides {linked.ref}. The chair may recommend {linked.proposed_label.toLowerCase()} or less, and cannot be the person who reported or raised it.
                </p>
              )}
            </div>
          )}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor="dr-hearing">Hearing held on</Label>
              <Input id="dr-hearing" type="date" max={todaySast()} value={f.hearing_on} onChange={set('hearing_on')} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="dr-rec-on">Recommendation dated</Label>
              <Input id="dr-rec-on" type="date" max={todaySast()} value={f.recommended_on} onChange={set('recommended_on')} />
            </div>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor="dr-chair">Chaired by</Label>
              <NativeSelect id="dr-chair" value={f.chair_person_id} onChange={set('chair_person_id')}>
                <option value="">Choose who chaired</option>
                {page.chairs.map((c) => <option key={c.id} value={c.id}>{c.name ?? 'Unnamed'}{c.is_chair ? ' (hearing chair)' : ''}</option>)}
              </NativeSelect>
            </div>
            <div className="space-y-1">
              <Label htmlFor="dr-sanction">Recommended sanction</Label>
              <NativeSelect id="dr-sanction" value={f.sanction}
                onChange={(e) => { const v = e.target.value; setF((x) => ({ ...x, sanction: v, recommended_months: isWarning(v) ? String(page.default_months?.[v] ?? '') : '' })) }}>
                <option value="">Choose one</option>
                {page.sanctions.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </NativeSelect>
            </div>
          </div>
          {isWarning(f.sanction) && (
            <div className="space-y-1">
              <Label htmlFor="dr-months">How many months the chair said it stays on his record</Label>
              <Input id="dr-months" type="number" min={1} max={24} value={f.recommended_months} onChange={set('recommended_months')} className="sm:max-w-[10rem]" />
              <p className="text-xs text-muted-foreground">Copy the number from the recommendation. The standard period is filled in for you, but the chair's number is the one that counts.</p>
            </div>
          )}
          <div className="space-y-1">
            <Label htmlFor="dr-charge">What the hearing was about</Label>
            <Input id="dr-charge" value={f.charge} onChange={set('charge')} maxLength={600}
              placeholder="One sentence, for example: unauthorised use of the vehicle on 20 September" />
          </div>
          <div className="space-y-1">
            <Label htmlFor="dr-summary">The finding, in a line or two (optional)</Label>
            <Textarea id="dr-summary" rows={2} value={f.summary} onChange={set('summary')} maxLength={1200} />
          </div>
          <div className="space-y-1">
            <Label htmlFor="dr-file">The recommendation document</Label>
            <Input id="dr-file" type="file" accept="application/pdf,image/jpeg,image/png" onChange={(e) => setDoc(e.target.files?.[0] ?? null)} />
            <p className="text-xs text-muted-foreground">The PDF the chair sent, or a photo or screenshot of it (JPG or PNG). It is sent for signature exactly as it is.</p>
            {docProblem && <p className="text-xs text-rose-700 dark:text-rose-300">{docProblem}</p>}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button variant="brand" disabled={!ready || busy} onClick={() => void submit()}>
            {busy && <Spinner className="mr-1 h-4 w-4" />} Upload and file it
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
